import { trackConversion } from "./Analytics";
import FbPixel from "@/utils/FbPixel";
import { useConsentStore } from "@/libs/consent/consentStore";
import { ConsentCategory } from "@/libs/consent/constants";
import {
  asCurrencyLabel,
  GePurchasePayload,
} from "@/libs/global-e/checkout-step-payload";

// GE 국제 체크아웃 주문의 **Meta `Purchase` · Google Ads 전환** 발화 (#5778).
// 호출처는 `src/libs/global-e/checkout-analytics.ts` 의 CONFIRMATION 콜백 하나.
//
// ## ★GA4 `purchase` 는 여기서 쏘지 않는다
//
// GA4 는 **mgmt 서버사이드 MP** 소관이다. 이유는 식별자다 — GA4 의 dedup 계약은
// `transaction_id = order.orderId`(주문번호)인데(`Analytics.ts` 의 `purchased` 주석, #5037),
// **발화 시점에 그 값이 존재하지 않는다**(실측: 주문번호는 CONFIRMATION 기준 T+16s 에 생긴다).
// 여기서 GE OrderId 로 쏘면 mgmt MP 와 축이 갈려 **같은 거래가 두 건**이 된다.
//
// Meta·Ads 는 사정이 다르다 — CAPI 짝이 없고 발화처가 여기 하나뿐이라 **self-consistent 하면
// 충분**하다. 그래서 GE OrderId 를 식별자로 쓴다(GE 공식 샘플도 `transaction_id: data.OrderId`).
//
// GA4 축은 별도 경로로 산다: 카트 sync 가 `gaClientId`/`gaSessionId` 를 `carts[cartToken]` 에
// 적어두고(`ga-cookie.ts` 의 `readGaIdentifiersFromBrowser` → `CartApi.ts` 의 `postGuestCart`),
// mgmt 가 GE draft 생성 시 옮겨 실어 기존 발화기가 그대로 커버한다(#5778, mgmt#1276).
//
// ## `track-purchase.ts` 와의 관계
//
// 자체 결제의 짝이다. 그쪽은 `paymentMethod === "GlobalE"` 를 early return 해서 **발화처를 하나로
// 고정**한다 — 안 그러면 주문 이력에서 30분 안에 재진입할 때 Meta 가 이중 계수된다(event_id 가
// `order.id` vs GE OrderId 로 갈린다).
//
// 30분 window 가드는 없다. 그 가드는 "과거 주문 재열람"을 거르는 것인데 이 경로는 결제 직후
// CONFIRMATION 에서만 불린다(`IsPageReload` 도 호출부가 이미 거른다).

const PURCHASE_FIRED_KEY_PREFIX = "bh-purchase-fired:";

function hasAlreadyFired(key: string): boolean {
  try {
    return localStorage.getItem(PURCHASE_FIRED_KEY_PREFIX + key) !== null;
  } catch {
    return false;
  }
}

function markFired(key: string): void {
  try {
    localStorage.setItem(PURCHASE_FIRED_KEY_PREFIX + key, String(Date.now()));
  } catch {
    // localStorage 불가(사파리 프라이빗 등) — 가드 생략
  }
}

export function trackGePurchase(payload: GePurchasePayload): void {
  const { consent, hasConsented } = useConsentStore.getState();

  // 동의 전에는 아무것도 보내지 않는다 — `trackPurchase` 와 같은 기본값.
  // ★서버가 아니라 브라우저에서 쏘는 이유가 이 줄이다: GE 주문의 다수가 GDPR 관할이고,
  // 서버 발화는 이 게이트를 통째로 우회한다(`docs/consent.md`).
  if (!hasConsented) return;
  if (!consent[ConsentCategory.Advertising]) return;

  if (hasAlreadyFired(payload.orderId)) return;
  markFired(payload.orderId);

  const {
    orderId,
    items,
    currency,
    subtotalAmount,
    shippingPrice,
    shippingCountry,
  } = payload;

  trackConversion(subtotalAmount, orderId, currency);

  FbPixel.purchased({
    id: orderId,
    items,
    gameItems: [],
    currency: asCurrencyLabel(currency),
    shippingCountry,
    subtotalAmount,
    shippingPrice,
    referralCode: "",
  });
}
